import { type FC } from 'react';

import { useFormatter } from '@eneco/i18n';
import { Box, Text } from '@eneco/sparky';

import { buildNettingDisclosure } from './buildNettingDisclosure';
import { NETTING_END_DATE } from './netting';
import { nettingDisclosureCopy } from './nettingDisclosure.copy';
import { getNettingFigures, isNettingDisclosureAvailable } from './nettingFigures';
import type { Offer } from '../../types';
import { PriceDetails } from '../PriceDetails/PriceDetails';

interface Props {
  offer?: Offer;
}

/**
 * The redelivery section of the electricity price breakdown, split into the last netted year and the first
 * year without netting. Renders inside a `PriceDetails` card, in place of the plain redelivery section.
 */
export const NettingDisclosure: FC<Props> = ({ offer }) => {
  const { currency, unit, date } = useFormatter();

  const disclosure = buildNettingDisclosure(getNettingFigures(offer), {
    euroHighPrecision: currency.euroHighPrecision,
    euroFiveDecimals: currency.euroFiveDecimals,
    electricity: unit.electricity,
    longDate: date.long,
  });

  if (!disclosure) {
    return null;
  }

  return <PriceDetails.Content summary={disclosure.summary} details={disclosure.details} />;
};

/** One-line notice for the offer card, shown whenever the price breakdown carries the disclosure. */
export const NettingDisclosureNote: FC<Props> = ({ offer }) => {
  const { date } = useFormatter();

  if (!isNettingDisclosureAvailable(offer)) {
    return null;
  }

  return (
    <Box paddingTop={{ initial: '3', md: '0' }}>
      <Text size="BodyXS" color="textLowEmphasis">
        {nettingDisclosureCopy.offerCardNote(date.long(NETTING_END_DATE))}
      </Text>
    </Box>
  );
};
